'use client';

/**
 * Период и разрезы отчётов, живущие в адресной строке.
 *
 * Как и в списке звонков: ссылку на отчёт можно переслать, «назад» вернёт
 * прошлый период, перезагрузка не сбрасывает работу.
 *
 * Здесь хранится **то, что мы послали**, а не то, что вернул сервер.
 * Границы периода считает он, в поясе тенанта, и приходят они в каждом
 * ответе: подпись на экране всегда из `period`, никогда из этих полей.
 * Пустые `from`/`to` — законное состояние: сервер возьмёт текущий
 * календарный месяц тенанта, а не месяц по часам браузера.
 */

import { useCallback, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { isoDate } from '@/lib/format';
import type { ReportFilters } from '@/lib/api/types';

/**
 * Пресеты только проставляют `from`/`to` — и делают это по часам браузера,
 * потому что другого времени у клиента нет. Расхождение в пределах пояса
 * сервер поправит сам: голая дата толкуется в поясе тенанта.
 */
export type PeriodPreset = 'today' | 'week' | 'month' | 'quarter' | 'custom';

export function presetRange(preset: PeriodPreset): { from?: string; to?: string } {
  const now = new Date();
  // `to` не включается, поэтому правая граница — завтра.
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  switch (preset) {
    case 'today':
      return { from: isoDate(now), to: isoDate(tomorrow) };
    case 'week': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { from: isoDate(start), to: isoDate(tomorrow) };
    }
    case 'month':
      // Пустые границы: месяц отмерит сервер, в поясе тенанта.
      return { from: undefined, to: undefined };
    case 'quarter': {
      const start = new Date(now);
      start.setDate(start.getDate() - 89);
      return { from: isoDate(start), to: isoDate(tomorrow) };
    }
    default:
      return {};
  }
}

export function useReportFilters() {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();

  const filters: ReportFilters = useMemo(
    () => ({
      from: params.get('from') || undefined,
      to: params.get('to') || undefined,
      unit: params.get('unit') || undefined,
      source: params.get('source') || undefined,
      employee: params.get('employee') || undefined,
    }),
    [params]
  );

  const patch = useCallback(
    (changes: Partial<ReportFilters>) => {
      const next = new URLSearchParams(params.toString());
      for (const [key, value] of Object.entries(changes)) {
        if (value === undefined || value === null || value === '') next.delete(key);
        else next.set(key, String(value));
      }
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [params, pathname, router]
  );

  /**
   * Какой пресет выбран сейчас. Считаем сравнением с диапазоном пресета, а не
   * храним отдельным параметром: иначе после правки дат вручную подсветка
   * осталась бы на «неделе», хотя период уже другой.
   */
  const preset: PeriodPreset = useMemo(() => {
    if (!filters.from && !filters.to) return 'month';
    for (const candidate of ['today', 'week', 'quarter'] as const) {
      const range = presetRange(candidate);
      if (range.from === filters.from && range.to === filters.to) return candidate;
    }
    return 'custom';
  }, [filters.from, filters.to]);

  return { filters, patch, preset };
}
